
import React, { createContext, useContext, useState, useEffect } from 'react';

interface SuperAdminUser {
  id: string;
  email: string;
  role: string;
}

interface SuperAdminAuthContextType {
  user: SuperAdminUser | null;
  login: (pin: string) => Promise<{ error: string | null }>;
  logout: () => void;
  loading: boolean;
}

const SuperAdminAuthContext = createContext<SuperAdminAuthContextType | undefined>(undefined);

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext);
  if (context === undefined) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
};

export const SuperAdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SuperAdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Super Admin PIN - Change this to your desired 4-digit PIN
  const SUPER_ADMIN_PIN = '9876';

  useEffect(() => {
    // Check if admin is logged in on page load
    const adminData = localStorage.getItem('super_admin_auth');
    if (adminData) {
      try {
        const parsedAdmin = JSON.parse(adminData);
        setUser(parsedAdmin);
      } catch (error) {
        localStorage.removeItem('super_admin_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = async (pin: string) => {
    try {
      if (pin === SUPER_ADMIN_PIN) {
        const adminData = {
          id: 'super-admin-001',
          email: 'admin@autocare.com',
          role: 'super_admin'
        };
        
        setUser(adminData);
        localStorage.setItem('super_admin_auth', JSON.stringify(adminData));
        return { error: null };
      } else {
        return { error: 'Invalid PIN' };
      }
    } catch (error) {
      return { error: 'Login failed' };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('super_admin_auth');
  };

  return (
    <SuperAdminAuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </SuperAdminAuthContext.Provider>
  );
};
